/**
 * `<dismiss-button>`
 *
 * A small close (×) control. Clicking its button hides the closest section for
 * the rest of the browsing session. The choice is remembered in `sessionStorage`
 * (keyed per block) so the section stays hidden across page loads until the tab
 * is closed, then returns on the next visit.
 *
 * Skipped while `Shopify.designMode` is active so merchants can always see and
 * configure the block in the theme editor.
 *
 * Attributes:
 *   data-key - Storage key identifying this dismissible (required; the block
 *              passes its `block.id`).
 */
class DismissButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #target = null;

  /** Resolve the target, restore a prior dismissal, and wire up the click handler. */
  connectedCallback() {
    // Re-create per connection so listeners re-bind cleanly after a move/re-render.
    this.#abortController = new AbortController();

    this.#button = this.querySelector('button');
    this.#target = this.#resolveTarget();
    if (this.#button == null || this.#target == null) return;

    // Never hide in the theme editor — the merchant must be able to see it.
    if (window.Shopify?.designMode) return;

    if (this.#isDismissed()) {
      this.#hide();
      return;
    }

    this.#button.addEventListener('click', this.#onClick, {
      signal: this.#abortController.signal,
    });
  }

  /** Tear down listeners when the element leaves the DOM. */
  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * The element this control hides: the closest section it sits in.
   * @returns {HTMLElement | null}
   */
  #resolveTarget() {
    return this.closest('.shopify-section');
  }

  /**
   * Session-storage key for this dismissible. MUST match the format used by the
   * anti-flash inline script in blocks/dismiss-button.liquid
   * (`dismiss-button:dismissed:<block id>`) — change both together.
   * @returns {string}
   */
  #storageKey() {
    return `dismiss-button:dismissed:${this.dataset.key ?? ''}`;
  }

  /** @returns {boolean} */
  #isDismissed() {
    try {
      return sessionStorage.getItem(this.#storageKey()) === '1';
    } catch {
      // Storage blocked (private mode, etc.) — treat as not dismissed.
      return false;
    }
  }

  /** Hide the target section. */
  #hide() {
    if (this.#target) this.#target.hidden = true;
  }

  /**
   * Move focus off the section we're about to remove so a keyboard/screen-reader
   * user isn't silently dropped to `<body>`. Prefer the next section, then the
   * main landmark; do nothing if neither exists.
   */
  #moveFocusOut() {
    const fallback = this.#target?.nextElementSibling ?? document.querySelector('main');
    if (!(fallback instanceof HTMLElement)) return;

    // Only elements that aren't already focusable need a temporary tabindex, and
    // we remove it again on blur so the element is left exactly as we found it.
    const needsTabindex = !fallback.hasAttribute('tabindex');
    if (needsTabindex) {
      fallback.setAttribute('tabindex', '-1');
      fallback.addEventListener(
        'blur',
        () => fallback.removeAttribute('tabindex'),
        { once: true }
      );
    }
    fallback.focus({ preventScroll: true });
  }

  /** Dismiss on click: hide the section, restore focus, and remember the choice. */
  #onClick = () => {
    this.#moveFocusOut();
    this.#hide();
    try {
      sessionStorage.setItem(this.#storageKey(), '1');
    } catch {
      // Storage unavailable — the section still hides for this view.
    }
  };
}

if (!customElements.get('dismiss-button')) {
  customElements.define('dismiss-button', DismissButton);
}
